import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logout } from "../../store/slices/authSlice.ts";
import { RootState } from "../../store/index.ts";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import { fetchPartnerByUser } from "../../services/application.service.ts";
import { getProfileImageByCurp } from "../../services/profileImageCache.ts";

const EMPTY_AVATAR_SRC = `${process.env.PUBLIC_URL || ""}/assets/img/avatar.jpg`;

const DropdownComponent: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user, partner } = useSelector((state: RootState) => state.auth);
  const [profileUrl, setProfileUrl] = React.useState<string | null>(null);

  const displayName = React.useMemo(() => {
    const first = toCamelCase(String(user?.name || "").trim());
    const last = toCamelCase(
      String(
        (user as { lastName?: string; last_name?: string })?.lastName ??
          (user as { last_name?: string })?.last_name ??
          ""
      ).trim()
    );
    return [first, last].filter(Boolean).join(" ") || "Usuario";
  }, [
    user?.name,
    (user as { lastName?: string; last_name?: string })?.lastName,
    (user as { last_name?: string })?.last_name,
  ]);

  const avatarSrc = profileUrl?.trim() || EMPTY_AVATAR_SRC;

  React.useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      const avatar = (user as { avatar?: string })?.avatar?.trim();
      if (avatar) {
        setProfileUrl(avatar);
        return;
      }

      const curpFromPartner = partner?.person?.curp;
      if (curpFromPartner) {
        const url = await getProfileImageByCurp(curpFromPartner);
        if (!cancelled) setProfileUrl(url);
        return;
      }

      const userId = Number(user?.id);
      if (!userId) {
        if (!cancelled) setProfileUrl(null);
        return;
      }

      try {
        const linkedPartner = await fetchPartnerByUser(userId);
        const curp = linkedPartner?.person?.curp;
        if (curp) {
          const url = await getProfileImageByCurp(curp);
          if (!cancelled) setProfileUrl(url);
          return;
        }
      } catch {
        /* sin partner vinculado */
      }

      if (!cancelled) setProfileUrl(null);
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
  }, [user?.id, (user as { avatar?: string })?.avatar, partner?.person?.curp]);

  return (
    <div className="dropdown">
      <div
        className="dropdown-toggle d-flex align-items-center md-justify-content-center cursor-pointer"
        id="dropdownMenuButton"
        data-bs-toggle="dropdown"
        aria-expanded="false"
      >
        <p
          className="me-2 mb-0 text-white fw-600 text-center"
          style={{ fontSize: "0.8rem", lineHeight: 1.2 }}
        >
          {displayName}
        </p>
        <img
          src={avatarSrc}
          alt=""
          className="rounded-circle"
          style={{
            width: 28,
            height: 28,
            objectFit: "cover",
            flexShrink: 0,
          }}
          onError={(e) => {
            e.currentTarget.src = EMPTY_AVATAR_SRC;
          }}
        />
      </div>

      <ul className="dropdown-menu" aria-labelledby="dropdownMenuButton">
        {["Administrador", "Administrador-"].includes(user.rol) && (
          <React.Fragment>
            <li>
              <a
                className="dropdown-item"
                onClick={() => navigate("/reportes")}
              >
                <i className="fa-solid fa-clipboard-list"></i>&nbsp; Reportes
              </a>
            </li>
            <li style={{ position: "relative" }}>
              <a
                className="dropdown-item"
                href="/revision-documentos"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/revision-documentos");
                }}
              >
                <i className="fa-solid fa-clipboard-check"></i>&nbsp; Revisión
                de Documentos
              </a>
              <span
                style={{
                  position: "absolute",
                  top: "50%",
                  right: "10px",
                  transform: "translateY(-50%)",
                  background:
                    "linear-gradient(135deg, #50B7C0 0%, #3d9aa3 100%)",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: "15px",
                  zIndex: 10,
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                }}
              >
                Nuevo
              </span>
            </li>
            <li>
              <a
                className="dropdown-item"
                onClick={() => navigate("/graficas")}
              >
                <i className="fa-solid fa-chart-line"></i>&nbsp; Gráficas
              </a>
            </li>
            <li>
              <a
                className="dropdown-item"
                href="#"
                onClick={() => navigate("/configuracion")}
              >
                <i className="fa-solid fa-gear"></i>&nbsp; Configuración
              </a>
            </li>
            <li>
              <a
                className="dropdown-item"
                href="#"
                onClick={() => navigate("/registro-de-entradas")}
              >
                <i className="fa-solid fa-qrcode"></i>&nbsp; Registros de
                entrada
              </a>
            </li>
            <li>
              <a
                className="dropdown-item"
                href="#"
                onClick={() => navigate("/quejas")}
              >
                <i className="fa-solid fa-comment-dots"></i>&nbsp; Quejas
              </a>
            </li>
          </React.Fragment>
        )}
        <li>
          <a
            className="dropdown-item"
            href="#/"
            onClick={() => {
              dispatch(logout());
              navigate("/");
            }}
          >
            <i className="fa-solid fa-right-from-bracket"></i>&nbsp;Cerrar
            sesión
          </a>
        </li>
      </ul>
    </div>
  );
};

export default DropdownComponent;
